(() => {
  if (window.__bitcoWishlistPageBound) return;
  window.__bitcoWishlistPageBound = true;

  const KEY = 'bitco:wishlist';
  const SECTION_ID = 'bitco-wishlist-card';
  const CARD_SELECTOR = '[data-bitco-wishlist-card]';

  // Same read contract as assets/bitco-wishlist.js: null means storage itself is
  // unavailable (private browsing, quota) — treated as "nothing to fetch" here rather
  // than throwing, since this page can't do anything useful without storage anyway.
  const readHandles = () => {
    let raw = null;
    try {
      raw = localStorage.getItem(KEY);
    } catch (e) {
      return [];
    }
    if (raw === null) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  // Fetches one product's card markup. Resolves to null (never rejects) on any
  // failure — a 404 (deleted/unpublished product), a network error, or a response with
  // no card element (sections/bitco-wishlist-card.liquid renders nothing when `product`
  // is blank) — so a single stale/broken handle can never block the rest of the grid.
  const fetchCard = (handle) =>
    fetch(`/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`)
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.querySelector(CARD_SELECTOR);
      })
      .catch(() => null);

  const render = (root) => {
    const grid = root.querySelector('[data-bitco-wishlist-grid]');
    const empty = root.querySelector('[data-bitco-wishlist-empty]');
    const loading = root.querySelector('[data-bitco-wishlist-loading]');
    if (!grid) return;

    const handles = readHandles();

    if (handles.length === 0) {
      grid.hidden = true;
      grid.innerHTML = '';
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    if (loading) loading.hidden = false;
    grid.hidden = true;

    // Fetched in parallel (Promise.all), not one-at-a-time — N requests either way, but
    // this keeps total wait time close to the single slowest request rather than the
    // sum of all of them.
    const requestedHandles = handles.slice();
    Promise.all(requestedHandles.map(fetchCard)).then((cards) => {
      // A wishlist toggle (or a fresh render call) may have started a newer request while
      // this one was in flight; if the saved handles changed since we started, drop this
      // stale batch instead of rendering out-of-date results over a newer render pass.
      if (JSON.stringify(readHandles()) !== JSON.stringify(requestedHandles)) return;

      grid.innerHTML = '';
      let rendered = 0;
      cards.forEach((card) => {
        if (!card) return;
        // Every card injected here corresponds 1:1 to a currently-saved handle, so its
        // heart toggle is always in the "active" state on arrival — set that directly
        // instead of dispatching a synthetic `shopify:section:load` (which would also
        // re-trigger every OTHER section's own load listener sitewide just to sync one
        // button's aria-pressed/class here).
        card.querySelectorAll('[data-bitco-wishlist-toggle]').forEach((btn) => {
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
        });
        grid.appendChild(card);
        rendered += 1;
      });

      if (loading) loading.hidden = true;
      if (rendered === 0) {
        grid.hidden = true;
        if (empty) empty.hidden = false;
      } else {
        grid.hidden = false;
      }
    });
  };

  const init = () => {
    const root = document.querySelector('[data-bitco-wishlist-page]');
    if (!root) return;
    render(root);
    // Re-query the root on every event rather than closing over the initial reference:
    // a theme-editor section reload replaces the section's DOM, so the captured `root`
    // becomes a detached orphan — writing to it would leave the live page uninitialised.
    // Matches bitco-wishlist.js's pattern of a fresh document query on every sync.
    document.addEventListener('bitco:wishlist-change', () => {
      const current = document.querySelector('[data-bitco-wishlist-page]');
      if (current) render(current);
    });
    document.addEventListener('shopify:section:load', (event) => {
      // Only react when the wishlist section itself was (re)loaded, and render into the
      // freshly-loaded root — never a stale closured reference to the replaced DOM.
      const reloaded = event.target && event.target.querySelector && event.target.querySelector('[data-bitco-wishlist-page]');
      if (reloaded) render(reloaded);
    });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
